import styled from "@emotion/styled";
import {
  ApiOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { Collapse, Divider, Space, Tag, Typography } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiDocsEndpointSection,
  type FieldRow,
  type QueryParamRow,
} from "./ApiDocsEndpointSection";

const { Title, Paragraph, Text } = Typography;

const BOOKINGS_PATH = "/functions/v1/api-public-bookings";
const PRODUCTS_PATH = "/functions/v1/api-public-products";

const BOOKING_FIELDS_LIST = [
  "id",
  "reference",
  "status",
  "created_at",
  "eta",
  "notes",
  "rejection_reason",
  "origin_pickup_date",
  "confirmed_pick_up_date",
  "origin_received_date",
  "export_clearance",
  "pick_up_fee",
  "commodity_inspection",
  "post_export_license",
  "unloading_status",
  "unloaded_at",
  "total_actual_cartons",
  "total_actual_pallets",
  "unloading_notes",
  "commercial_invoice_file",
  "leancustoms_order_id",
  "leancustoms_declaration_id",
  "transport_type",
  "booking_type",
  "origin_port",
  "destination_port",
  "incoterm",
  "company",
  "supplier",
  "agent",
  "container",
  "deliveries",
  "products",
  "purchase_orders",
] as const;

const PRODUCT_FIELDS_LIST = [
  "id",
  "created_at",
  "company_id",
  "name",
  "ean",
  "hs_code",
  "pieces_per_carton",
  "weight_per_carton",
  "carton_length",
  "carton_width",
  "carton_height",
  "unit_price",
  "picqer_product_id",
  "channeldock_product_id",
  "stock",
] as const;

const DocShell = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const DocHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
`;

const DocBody = styled.div`
  padding: 20px 24px 28px;
`;

const Section = styled.section`
  margin-top: 28px;
  &:first-of-type {
    margin-top: 0;
  }
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8c8c8c;
  margin-bottom: 10px;
`;

const CodeBlock = styled.pre`
  margin: 0;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas,
    monospace;
  background: #f6f8fa;
  color: #24292f;
  border: 1px solid #d0d7de;
`;

export type AccountApiDocsReferenceProps = {
  bookingsUrl: string;
  bookingsCurl: string;
  productsUrl: string;
  productsCurl: string;
};

function buildBookingsResponseExampleJson(): string {
  const sample = {
    id: 3888,
    reference: "PO-2025-0142",
    status: "in_transit",
    created_at: "2025-04-12T10:22:00.000Z",
    eta: "2025-05-01",
    notes: null,
    rejection_reason: null,
    origin_pickup_date: null,
    confirmed_pick_up_date: null,
    origin_received_date: null,
    export_clearance: true,
    pick_up_fee: null,
    commodity_inspection: null,
    post_export_license: null,
    unloading_status: "pending",
    unloaded_at: null,
    total_actual_cartons: null,
    total_actual_pallets: null,
    unloading_notes: null,
    commercial_invoice_file: null,
    leancustoms_order_id: null,
    leancustoms_declaration_id: null,
    transport_type: "Sea freight (SEA)",
    booking_type: "FCL",
    origin_port: "Shanghai, CN",
    destination_port: "Rotterdam, NL",
    incoterm: { name: "FOB", description: null },
    company: { name: "Example BV" },
    supplier: {
      name: "ACME Trading",
      email: "orders@example.com",
      phone: null,
      country: "CN",
      province: null,
      city: "Shanghai",
      address: "…",
    },
    agent: { name: "Jane Doe", email: "jane@example.com", phone: null },
    container: {
      id: 9001,
      reference: "MSCU1234567",
      bl_number: "ONEY1234567890",
      track_and_trace: "https://example.com/track/…",
      etd: "2025-04-01",
      eta: "2025-04-28",
      eta_customer: "2025-05-01",
      status: "in_transit",
      origin_port: "Shanghai, CN",
      destination_port: "Rotterdam, NL",
      master_container: null,
    },
    deliveries: [
      {
        id: 1,
        street_name: "Industrieweg",
        house_number: "10",
        city: "Rotterdam",
        postal_code: "3044 BC",
        country: "NL",
        shipment_status: "planned",
        scheduled_delivery_date: "2025-05-03",
      },
    ],
    products: [
      {
        id: 501,
        name: "Sample goods",
        hs_code: "851762",
        ean: 8712345678901,
        pieces: 100,
        cartons: 5,
        gross_weight: 120.5,
        cbm: 1.2,
        value: 5000,
        shipping_label: null,
      },
    ],
    purchase_orders: [],
  };
  return JSON.stringify(
    {
      data: [sample],
      meta: {
        limit: 50,
        offset: 0,
        count: 120,
        fields: [...BOOKING_FIELDS_LIST],
      },
    },
    null,
    2
  );
}

function buildProductsResponseExampleJson(): string {
  const sample = {
    id: 101,
    created_at: "2025-03-01T12:00:00.000Z",
    company_id: 1,
    name: "Sample product",
    ean: 8712345678901,
    hs_code: null,
    pieces_per_carton: 12,
    weight_per_carton: 8.5,
    carton_length: 40,
    carton_width: 30,
    carton_height: 25,
    unit_price: 19.99,
    picqer_product_id: 555,
    channeldock_product_id: null,
    stock: 140,
  };
  return JSON.stringify(
    {
      data: [sample],
      meta: {
        limit: 100,
        offset: 0,
        count: 42,
        wms_provider: "picqer",
        wms_connected: true,
        fields: [...PRODUCT_FIELDS_LIST],
      },
    },
    null,
    2
  );
}

export function AccountApiDocsReference({
  bookingsUrl,
  bookingsCurl,
  productsUrl,
  productsCurl,
}: AccountApiDocsReferenceProps) {
  const { t } = useTranslation();

  const functionsOrigin = useMemo(() => {
    try {
      return new URL(bookingsUrl).origin;
    } catch {
      return "";
    }
  }, [bookingsUrl]);

  const bookingsQueryData: QueryParamRow[] = useMemo(
    () => [
      {
        key: "limit",
        name: "limit",
        type: "integer",
        defaultVal: "50",
        description: t("account.apiDocsBookingsParamLimitDesc"),
      },
      {
        key: "offset",
        name: "offset",
        type: "integer",
        defaultVal: "0",
        description: t("account.apiDocsBookingsParamOffsetDesc"),
      },
    ],
    [t]
  );

  const productsQueryData: QueryParamRow[] = useMemo(
    () => [
      {
        key: "limit",
        name: "limit",
        type: "integer",
        defaultVal: "100",
        description: t("account.apiDocsProductsParamLimitDesc"),
      },
      {
        key: "offset",
        name: "offset",
        type: "integer",
        defaultVal: "0",
        description: t("account.apiDocsProductsParamOffsetDesc"),
      },
    ],
    [t]
  );

  const bookingFieldRows: FieldRow[] = useMemo(() => {
    const typeFor = (field: (typeof BOOKING_FIELDS_LIST)[number]): string => {
      if (field === "id") return t("account.apiDocsBookingsTypeId");
      if (field === "export_clearance") return t("account.apiDocsBookingsTypeBooleanNull");
      if (field === "commodity_inspection") return t("account.apiDocsBookingsTypeBooleanNull");
      if (
        field === "pick_up_fee" ||
        field === "post_export_license" ||
        field === "total_actual_cartons" ||
        field === "total_actual_pallets"
      ) {
        return t("account.apiDocsBookingsTypeNumberNull");
      }
      if (field === "deliveries" || field === "products" || field === "purchase_orders") {
        return t("account.apiDocsBookingsTypeArray");
      }
      if (
        field === "incoterm" ||
        field === "company" ||
        field === "supplier" ||
        field === "agent" ||
        field === "container"
      ) {
        return t("account.apiDocsBookingsTypeObjectNullable");
      }
      if (
        field === "transport_type" ||
        field === "booking_type" ||
        field === "origin_port" ||
        field === "destination_port" ||
        field === "notes" ||
        field === "rejection_reason" ||
        field === "unloading_status" ||
        field === "unloading_notes" ||
        field === "commercial_invoice_file" ||
        field === "leancustoms_order_id" ||
        field === "leancustoms_declaration_id"
      ) {
        return t("account.apiDocsBookingsTypeStringNull");
      }
      return t("account.apiDocsBookingsTypeString");
    };
    return BOOKING_FIELDS_LIST.map((field) => ({
      key: field,
      field,
      type: typeFor(field),
    }));
  }, [t]);

  const productFieldRows: FieldRow[] = useMemo(
    () =>
      PRODUCT_FIELDS_LIST.map((field) => ({
        key: field,
        field,
        type:
          field === "stock"
            ? t("account.apiDocsProductsStockType")
            : t("account.apiDocsFieldTypeMixed"),
      })),
    [t]
  );

  const bookingsResponseJson = useMemo(() => buildBookingsResponseExampleJson(), []);
  const productsResponseJson = useMemo(() => buildProductsResponseExampleJson(), []);

  return (
    <DocShell>
      <DocHeader>
        <Space align="start" size="middle" wrap>
          <ApiOutlined
            style={{ fontSize: 30, color: "#1677ff", marginTop: 2 }}
            aria-hidden
          />
          <div style={{ flex: "1 1 200px" }}>
            <Space align="center" wrap size="small">
              <Title level={4} style={{ margin: 0 }}>
                {t("account.apiDocsReferenceTitle")}
              </Title>
              <Tag color="processing">{t("account.apiDocsReferenceVersion")}</Tag>
            </Space>
            <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
              {t("account.apiDocsReferenceSubtitle")}
            </Paragraph>
          </div>
        </Space>
      </DocHeader>

      <DocBody>
        <Section>
          <SectionLabel>{t("account.apiDocsSectionOverview")}</SectionLabel>
          <Paragraph style={{ marginBottom: 0 }}>{t("account.apiDocsIntro")}</Paragraph>
        </Section>

        <Section>
          <SectionLabel>
            <LinkOutlined style={{ marginRight: 6 }} />
            {t("account.apiDocsSectionBaseUrl")}
          </SectionLabel>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {t("account.apiDocsBaseUrlHint")}
          </Paragraph>
          <CodeBlock>
            <code>{functionsOrigin}</code>
          </CodeBlock>
        </Section>

        <Section>
          <SectionLabel>
            <SafetyCertificateOutlined style={{ marginRight: 6 }} />
            {t("account.apiDocsSectionAuth")}
          </SectionLabel>
          <Paragraph style={{ marginBottom: 12 }}>
            {t("account.apiDocsAuthHow")}
          </Paragraph>
          <CodeBlock>
            <code>{"Authorization: Bearer <api_key>"}</code>
          </CodeBlock>
        </Section>

        <Divider style={{ margin: "28px 0 20px" }} />

        <Section>
          <SectionLabel>{t("account.apiDocsEndpointsHeading")}</SectionLabel>

          <Collapse
            bordered={false}
            defaultActiveKey={["bookings"]}
            expandIconPosition="end"
            style={{ background: "transparent" }}
            items={[
              {
                key: "bookings",
                label: (
                  <Space wrap size="small" align="center">
                    <Tag color="success">GET</Tag>
                    <Text strong>{t("account.apiDocsEndpointBookingsTitle")}</Text>
                    <Text code style={{ fontSize: 13 }}>
                      {BOOKINGS_PATH}
                    </Text>
                  </Space>
                ),
                children: (
                  <ApiDocsEndpointSection
                    hideEndpointBar
                    path={BOOKINGS_PATH}
                    title={t("account.apiDocsEndpointBookingsTitle")}
                    intro={t("account.apiDocsEndpointBookingsIntro")}
                    fullUrl={bookingsUrl}
                    queryTitle={t("account.apiDocsBookingsQuery")}
                    queryData={bookingsQueryData}
                    fieldsTitle={t("account.apiDocsBookingsFieldsTitle")}
                    fieldRows={bookingFieldRows}
                    responseDesc={t("account.apiDocsBookingsResponseDesc")}
                    responseJson={bookingsResponseJson}
                    exampleTitle={t("account.apiDocsBookingsExampleTitle")}
                    curl={bookingsCurl}
                  />
                ),
              },
              {
                key: "products",
                label: (
                  <Space wrap size="small" align="center">
                    <Tag color="success">GET</Tag>
                    <Text strong>{t("account.apiDocsEndpointProductsTitle")}</Text>
                    <Text code style={{ fontSize: 13 }}>
                      {PRODUCTS_PATH}
                    </Text>
                  </Space>
                ),
                children: (
                  <ApiDocsEndpointSection
                    hideEndpointBar
                    path={PRODUCTS_PATH}
                    title={t("account.apiDocsEndpointProductsTitle")}
                    intro={t("account.apiDocsEndpointProductsIntro")}
                    fullUrl={productsUrl}
                    queryTitle={t("account.apiDocsProductsQueryTitle")}
                    queryData={productsQueryData}
                    fieldsTitle={t("account.apiDocsProductsFieldsTitle")}
                    fieldRows={productFieldRows}
                    responseDesc={t("account.apiDocsProductsResponseDesc")}
                    responseJson={productsResponseJson}
                    exampleTitle={t("account.apiDocsProductsExampleTitle")}
                    curl={productsCurl}
                  />
                ),
              },
            ]}
          />
        </Section>
      </DocBody>
    </DocShell>
  );
}
